import { Component, Show, createSignal, onMount } from "solid-js";
import { useParams } from "@solidjs/router";
import { Button, Spinner } from "../components/ui";
import { getInvite, acceptInvite } from "../api/invites";
import { getMe } from "../api/auth";
import { ApiError } from "../api/client";
import type { InvitePreview } from "../api/types";
import { logoSrc } from "../stores/themeStore";

const invalidReasonMessages: Record<string, string> = {
  INVITE_EXPIRED: "This invitation has expired.",
  INVITE_ALREADY_ACCEPTED: "This invitation has already been used.",
  INVITE_REVOKED: "This invitation is no longer valid.",
};

function describeInviteError(err: unknown): string {
  const code =
    err instanceof ApiError
      ? (err.data as { code?: string } | undefined)?.code
      : undefined;
  return (
    (code ? invalidReasonMessages[code] : undefined) ??
    "This invitation link is not valid."
  );
}

/**
 * Public invite acceptance page (FLOW-630).
 *
 * Signed-out visitors get a full-page navigation to GitHub OAuth (never a
 * fetch — the browser needs to actually hop to github.com), carrying the
 * invite token through so the backend can complete the flow after auth.
 * Signed-in visitors get a one-click accept.
 */
export const InvitePage: Component = () => {
  const params = useParams<{ token: string }>();
  const token = () => params.token;

  const [loading, setLoading] = createSignal(true);
  const [invite, setInvite] = createSignal<InvitePreview | null>(null);
  const [invalidReason, setInvalidReason] = createSignal<string | null>(null);
  const [signedIn, setSignedIn] = createSignal(false);
  const [accepting, setAccepting] = createSignal(false);
  const [acceptError, setAcceptError] = createSignal<string | null>(null);

  onMount(async () => {
    try {
      const preview = await getInvite(token());
      setInvite(preview);
    } catch (e) {
      setInvalidReason(describeInviteError(e));
      setLoading(false);
      return;
    }

    try {
      await getMe();
      setSignedIn(true);
    } catch {
      setSignedIn(false);
    }
    setLoading(false);
  });

  const handleAccept = async () => {
    setAccepting(true);
    setAcceptError(null);
    try {
      await acceptInvite(token());
      window.location.href = "/board";
    } catch (e) {
      setAcceptError(describeInviteError(e));
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div class="min-h-screen bg-paper flex items-center justify-center p-4">
      <div class="w-full max-w-sm">
        <div class="text-center mb-8">
          <img
            src={logoSrc()}
            alt="Flowicity"
            class="w-16 h-16 mx-auto mb-4 rounded-md"
          />
          <h1 class="text-2xl font-bold text-ink">You're invited</h1>
        </div>

        <Show when={loading()}>
          <div
            class="flex justify-center py-8"
            role="status"
            aria-live="polite"
          >
            <Spinner size="lg" />
            <span class="sr-only">Loading invitation…</span>
          </div>
        </Show>

        <Show when={!loading()}>
          <Show
            when={!invalidReason()}
            fallback={
              <div
                class="bg-surface rounded-md border border-line p-6"
                role="alert"
              >
                <p class="text-sm text-signal-red">{invalidReason()}</p>
              </div>
            }
          >
            <Show when={invite()}>
              {(inv) => (
                <div class="bg-surface rounded-md border border-line p-6">
                  <p class="text-sm text-ink-2 mb-1">
                    <span class="font-medium text-ink">
                      {inv().inviter_name}
                    </span>{" "}
                    invited you to join
                  </p>
                  <p class="text-lg font-semibold text-ink">
                    {inv().project_name}
                  </p>
                  <p class="text-sm text-ink-2 mb-5">
                    Accepting also adds you to the organization that owns this
                    project.
                  </p>

                  <Show when={acceptError()}>
                    <div
                      class="mb-4 p-3 bg-signal-red-tint border border-signal-red/25 rounded text-sm text-signal-red"
                      role="alert"
                    >
                      {acceptError()}
                    </div>
                  </Show>

                  <Show
                    when={signedIn()}
                    fallback={
                      <div>
                        <a
                          href={`/api/auth/github?invite=${encodeURIComponent(token())}`}
                          class="flex items-center justify-center gap-2 w-full px-4 py-3 bg-ink text-paper rounded focus-ring hover:bg-ink-2 transition-colors font-medium"
                        >
                          Continue with GitHub
                        </a>
                        <p class="mt-3 text-xs text-ink-3">
                          By continuing, you accept the{" "}
                          <a
                            href="/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-ink-2 hover:text-ink underline underline-offset-2"
                          >
                            Terms of Service
                          </a>{" "}
                          and{" "}
                          <a
                            href="/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-ink-2 hover:text-ink underline underline-offset-2"
                          >
                            Privacy Policy
                          </a>
                          .
                        </p>
                      </div>
                    }
                  >
                    <Button
                      variant="primary"
                      class="w-full"
                      onClick={handleAccept}
                      loading={accepting()}
                      disabled={accepting()}
                    >
                      Accept invitation
                    </Button>
                  </Show>
                </div>
              )}
            </Show>
          </Show>
        </Show>
      </div>
    </div>
  );
};
